import { LDContext, useLDClient } from 'launchdarkly-react-client-sdk';
import isEqual from 'lodash/isEqual';
import React, { Suspense, lazy, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import ErrorBoundaryFallback from 'components/atoms/ErrorBoundaryFallback';
import LoadingSpinner from 'components/composites/LoadingSpinner';
// Don't lazily import this or production build will fail
import ProtectedRoutesLoadingGate from 'components/loadingGates/ProtectedRoutesLoadingGate';
import BackOffice from 'routes/BackOffice';
import { loadMyInvitations } from 'store/modules/invitations/actions';
import { useMyUser, useMyUserIsInternalAdmin } from 'store/modules/myUser/hooks';
import {
  selectHasOnlyOneMembership,
  selectMyUserMembershipsEnterprises
} from 'store/modules/myUser/selectors';
import BackOfficeInvitation from 'types/models/back-office-invitation';
import constructEnterpriseDomain from 'utils/construct-enterprise-domain';
import getCurrentSubdomain from 'utils/get-current-subdomain';

import Integrations from './Integrations';
import InstallAgriWebb from './Integrations/AgriWebb/Install';
import NewUser from './NewUser';

const Enterprise = lazy(() => import('routes/Enterprise'));
const MyMemberships = lazy(() => import('routes/MyMemberships'));
const PageNotFound = lazy(() => import('routes/PageNotFound'));

const { REACT_APP_URL_DEFAULT_SUBDOMAIN } = process.env;

export const useLoadMyUserInvitations = ({ skip }: { skip: boolean }): BackOfficeInvitation[] | undefined => {
  const dispatch = useDispatch();
  const invitations: BackOfficeInvitation[] | undefined = useMyUser()?.invitations;

  useEffectOnce(() => {
    if (!skip) {
      dispatch(loadMyInvitations());
    }
  });

  return invitations;
};

function MainRoutes(): JSX.Element | null {
  const [isFeatureFlagReady, setIsFeatureFlagReady] = useState(false);

  // ==================== Start: THIS BLOCK ALLOWS FOR FEATURE FLAGS TO WORK ====================
  const ldClient = useLDClient();
  const myUser = useMyUser();

  useEffectOnce(() => {
    if (ldClient && myUser) {
      // We need a context for LaunchDarkly to evaluate and return the feature flags to the application.
      // Within an enterprise domain, we use the enterprise (see EnterpriseScaffoldRequest).
      // In other cases, we use the user.
      const ldContext: LDContext = {
        kind: 'user',
        key: myUser.id.toString(),
        name: myUser.email || myUser.mobileNumberFormatted || myUser.fullName
      };
      ldClient.identify(ldContext).then(() => setIsFeatureFlagReady(true));
    }
  });
  // ==================== End: THIS BLOCK ALLOWS FOR FEATURE FLAGS TO WORK ====================

  const myUserIsInternalAdmin = useMyUserIsInternalAdmin();
  const hasOnlyOneMembership = useSelector(selectHasOnlyOneMembership);
  const currentSubdomain = getCurrentSubdomain();
  const isAccessingSpecificEnterprise = Boolean(
    currentSubdomain && currentSubdomain !== REACT_APP_URL_DEFAULT_SUBDOMAIN
  );
  const pendingInvitations: BackOfficeInvitation[] | undefined =
    useLoadMyUserInvitations({ skip: isAccessingSpecificEnterprise });
  const myUserMembershipsEnterprises = useSelector(
    selectMyUserMembershipsEnterprises,
    isEqual
  );

  if (!isFeatureFlagReady) {
    return <LoadingSpinner useLogo />;
  }

  if (isAccessingSpecificEnterprise) {
    return (
      <Suspense fallback={<LoadingSpinner useLogo />}>
        <Enterprise />
      </Suspense>
    );
  }

  // Undefined means not-yet loaded
  if (!pendingInvitations) {
    return <LoadingSpinner useLogo />;
  }

  // (Unless on a specific enterprise subdomain)
  // If having pending invitations, the user must resolve them first (Join or Delete) before all other operations.
  // This ensures:
  // 1. The user does not forget about open invitations.
  // 2. The user is not redirected to an existing enterprise while trying to join a new one, e.g., https://farmbot.atlassian.net/browse/FMBT-7750
  if (pendingInvitations.length > 0) {
    return (
      <Suspense fallback={<LoadingSpinner useLogo />}>
        <Switch>
          <Route path="/enterprises" component={MyMemberships} />
          <Redirect from="/*" to="/enterprises" />
        </Switch>
      </Suspense>
    );
  }

  if (myUserIsInternalAdmin) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Switch>
          <Route path="/back-office">
            <BackOffice />
          </Route>
          <Route path="/connections">
            <Integrations />
          </Route>
          <Redirect from="/*" to="/back-office" />
        </Switch>
      </Suspense>
    );
  }

  if (hasOnlyOneMembership) {
    const currentEnterpriseSubdomain = constructEnterpriseDomain(myUserMembershipsEnterprises[0]);
    return (
      <Suspense fallback={<LoadingSpinner useLogo />}>
        <Switch>
          <Route path="/enterprises">
            <MyMemberships />
          </Route>
          <Route path="/connections">
            <Integrations />
          </Route>
          <Route
            path="/*"
            component={() => {
              window.location.replace(currentEnterpriseSubdomain);
              return null;
            }}
          />
        </Switch>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Switch>
        <Route path="/connections">
          <Integrations />
        </Route>
        <Route path="/*">
          <MyMemberships />
        </Route>
      </Switch>
    </Suspense>
  );
}

const Routes = () => (
  <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
    <Switch>
      {/* FIX: subdomain can access this link too */}
      <Route exact path="/invite/:token">
        <NewUser />
      </Route>
      <Route exact path="/connections/agriwebb/install">
        <InstallAgriWebb />
      </Route>
      <Route exact path="/404">
        <Suspense fallback={<LoadingSpinner useLogo />}>
          <PageNotFound />
        </Suspense>
      </Route>
      <Route path="/*">
        <ProtectedRoutesLoadingGate>
          <MainRoutes />
        </ProtectedRoutesLoadingGate>
      </Route>
    </Switch>
  </ErrorBoundary>
);

export default Routes;
